/* import __COLOCATED_TEMPLATE__ from './empty-state.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type Folder from 'embercom/models/content-service/folder';
import { EntityType } from 'embercom/models/data/entity-types';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type AddWebsiteModalService from 'embercom/services/add-website-modal-service';
import { SourceTypeName } from 'embercom/models/import-service/import-source';
import { SourceType } from 'embercom/models/import-service/import-source';
import { hasFeature } from 'embercom/helpers/has-feature';

interface Args {
  showNewContentModal: () => void;
  folder?: Folder;
  filtersApplied?: boolean;
}

const CONTENT_ENTITY_TYPES = [
  EntityType.ArticleContent,
  EntityType.InternalArticle,
  EntityType.ContentSnippet,
];

export default class EmptyState extends Component<Args> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare addWebsiteModalService: AddWebsiteModalService;

  get shouldShowNewInterface(): boolean {
    return (
      this.appService.app.canUseUnifiedContentUI &&
      this.args.folder?.sourceTypeName === SourceTypeName[SourceType.Web]
    );
  }

  get isWebContentFirstTimeSyncing(): boolean {
    return !!(
      hasFeature('team-knowledge-foundations-website-sync-progress', this.appService) &&
      this.args.folder &&
      this.args.folder.sourceTypeName === SourceTypeName[SourceType.Web] &&
      this.args.folder.firstTimeSyncing
    );
  }

  get contentTypeTiles() {
    let contentEntityTypes = [...CONTENT_ENTITY_TYPES];
    if (this.appService.app.canUseStandalone) {
      contentEntityTypes.removeObject(EntityType.InternalArticle);
    }
    if (this.appService.app.canUseUnifiedContentUI) {
      contentEntityTypes.push(EntityType.ExternalContent);
    }
    let localizationPrefix = 'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles';
    return contentEntityTypes.map((entityType) => {
      return {
        id: `${entityType}`,
        title: this.intl.t(`${localizationPrefix}.${objectNames[entityType]}.title`),
        entityType,
        description: this.appService.app.canUseStandalone
          ? this.intl.t(`${localizationPrefix}.${objectNames[entityType]}.description-ai-agent`)
          : this.intl.t(`${localizationPrefix}.${objectNames[entityType]}.description`),
      };
    });
  }

  get useTwoColumnGrid() {
    return this.contentTypeTiles.length > 3;
  }

  @action async createContent(entityType: EntityType) {
    if (entityType === EntityType.ExternalContent) {
      // External content will only be here if `canUseUnifiedContentUI` is true
      this.addWebsiteModalService.openAddWebsiteSheet();
      return;
    }

    await this.knowledgeHubService.createContent(entityType, {
      folder_id: this.args.folder?.id,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::EmptyState': typeof EmptyState;
    'knowledge-hub/empty-state': typeof EmptyState;
  }
}
